import React, { useEffect, useMemo, useState } from "react";
import { History, Info, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import { salaryService, salaryStructureService, branchService, payrollOpsService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import StructureRevisionDeleteModal from "../employee-management/salary-structure/StructureRevisionDeleteModal";
import { buildEmployeeStructureBreakdown } from "../../../utils/employeeStructureBreakdown";
import { findApplicableSalaryStructure } from "../../../utils/salaryStructureMatch";
import {
  applyManualOverrides,
  applyFormulaOverrides,
  extractManualOverridesFromSnapshot,
  structureRequiresManualInput,
  listManualFixedComponents,
  manualFixedColumnLabel,
  buildCompleteManualOverrides,
  buildEmployeeStructureSavePayload,
  structureCalcModeLabel,
} from "../../../utils/applyEmployeeStructureFromTemplate";
import { displayLabel } from "../../../utils/displayLabel";
import SalaryStructureCalculationResults from "../employee-management/salary-structure/SalaryStructureCalculationResults";
import { DsButton, DsIcon } from "../../../design-system";
import {
  ActionFormOverlay,
  ActionFormShell,
} from "../shared/ActionFormShell";
import {
  clampYm,
  clampYmd,
  resolveEmployeeMinDateValue,
  resolveEmployeeMinMonthValue,
} from "../../../utils/selectableDateBounds";

const REASON_OPTIONS = [
  "Performance",
  "Promotion",
  "As per benchmarking",
  "Market adjustment",
  "Retention",
  "Skill enhancement",
  "Other",
];

function numberToWords(num) {
  if (!num || num === 0) return "";
  const numValue = parseFloat(num);
  if (Number.isNaN(numValue) || numValue < 0) return "";
  const integerPart = Math.floor(numValue);
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convertHundreds = (n) => {
    let result = "";
    let x = n;
    if (x >= 100) {
      result += `${ones[Math.floor(x / 100)]} Hundred `;
      x %= 100;
    }
    if (x >= 20) {
      result += `${tens[Math.floor(x / 10)]} `;
      x %= 10;
    }
    if (x > 0) result += `${ones[x]} `;
    return result.trim();
  };
  if (integerPart === 0) return "";
  let result = "";
  let remaining = integerPart;
  if (remaining >= 10000000) {
    result += `${convertHundreds(Math.floor(remaining / 10000000))} Crore `;
    remaining %= 10000000;
  }
  if (remaining >= 100000) {
    result += `${convertHundreds(Math.floor(remaining / 100000))} Lakh `;
    remaining %= 100000;
  }
  if (remaining >= 1000) {
    result += `${convertHundreds(Math.floor(remaining / 1000))} Thousand `;
    remaining %= 1000;
  }
  if (remaining > 0) result += convertHundreds(remaining);
  return result.trim();
}

function calculatePercentageChange(current, revised) {
  const c = Number(current) || 0;
  const r = Number(revised) || 0;
  if (c <= 0) return null;
  return ((r - c) / c) * 100;
}

function formatIndianNumber(value) {
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN");
}

function toFirstOfMonth(iso) {
  if (!iso) return "";
  return `${String(iso).slice(0, 7)}-01`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDisplayDate(iso) {
  if (!iso) return "—";
  const raw = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-");
    return `${d}/${m}/${y}`;
  }
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch {
    return String(iso);
  }
}

function formatCreatorName(creator) {
  if (!creator) return "—";
  return [creator.name, creator.lastName].filter(Boolean).join(" ").trim() || "—";
}

function monthKeyFromIso(iso) {
  const s = String(iso || "").slice(0, 7);
  return /^\d{4}-\d{2}$/.test(s) ? s : "";
}

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

function joiningDateOf(employee) {
  return (
    employee?.joiningDate ||
    employee?.dateOfJoin ||
    employee?.joinDate ||
    employee?.user?.joiningDate ||
    ""
  );
}

/**
 * Floor for Increment Effective when no prior revision:
 * later of company service commencement and employee DOJ (not earlier of the two).
 */
function incrementEffectiveFloorIso(employee, allowMidMonth) {
  const floorYmd = resolveEmployeeMinDateValue(employee);
  if (floorYmd) {
    return allowMidMonth ? floorYmd : toFirstOfMonth(floorYmd);
  }
  const joinIso = String(joiningDateOf(employee) || "").slice(0, 10);
  const fallback = joinIso || todayIso();
  return allowMidMonth ? fallback : toFirstOfMonth(fallback);
}

function clampIncrementEffectiveIso(raw, employee, allowMidMonth) {
  const floorYmd = resolveEmployeeMinDateValue(employee);
  const floorYm = resolveEmployeeMinMonthValue(employee);
  let s = String(raw || "").trim();
  if (/^\d{4}-\d{2}$/.test(s)) {
    s = `${clampYm(s, floorYm, null)}-01`;
    return s;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const clamped = clampYmd(s, floorYmd || null, null);
    return allowMidMonth ? clamped : toFirstOfMonth(clamped);
  }
  return allowMidMonth ? s : toFirstOfMonth(s);
}

/** Pay Month: same as Increment Effective month, or any later unfinalised month. */
function buildPayMonthOptions({ finalizedSet, effectiveYm, bookingYm = null }) {
  const now = new Date();
  const curYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const start = String(effectiveYm || bookingYm || curYm).slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(start)) return [];

  let endYm = curYm;
  const booking = String(bookingYm || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(booking) && booking > endYm) endYm = booking;
  if (start > endYm) endYm = start;

  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = endYm.split("-").map(Number);
  const out = [];
  let y = sy;
  let m = sm;
  while (y * 12 + m <= ey * 12 + em) {
    const value = `${y}-${String(m).padStart(2, "0")}`;
    const payrollFinalized = finalizedSet.has(value);
    if (!payrollFinalized) {
      out.push({
        month: m,
        year: y,
        value,
        payrollFinalized: false,
        selectable: true,
        label: new Date(y, m - 1, 1).toLocaleString("default", {
          month: "long",
          year: "numeric",
        }),
      });
    }
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** Default Pay Month: saved revision booking month, or effective month when no prior increment. */
function resolveDefaultPayMonth({
  lastRevision,
  effectiveDate,
  payMonthOptions,
  bookingPayMonth = null,
}) {
  const selectable = (payMonthOptions || []).filter((o) => o.selectable);
  if (!selectable.length) return "";

  const revisionYm = String(lastRevision?.bookingPayMonth || "").slice(0, 7);
  const effectiveYm = monthKeyFromIso(effectiveDate);

  if (lastRevision?.id && /^\d{4}-\d{2}$/.test(revisionYm)) {
    const fromRevision = selectable.find((o) => o.value === revisionYm);
    if (fromRevision) return fromRevision.value;
  }

  // No prior salary revision increment — Pay Month follows Increment Effective month
  if (/^\d{4}-\d{2}$/.test(effectiveYm)) {
    const sameMonth = selectable.find((o) => o.value === effectiveYm);
    if (sameMonth) return sameMonth.value;
    const onOrAfter = selectable.find((o) => o.value >= effectiveYm);
    if (onOrAfter) return onOrAfter.value;
  }

  const screenYm = String(bookingPayMonth || currentYm()).slice(0, 7);
  return (
    selectable.find((o) => o.value === screenYm)?.value ||
    selectable[0]?.value ||
    ""
  );
}

/**
 * Salary Revision — Increment Effective Date, Pay Month, Amount or %age, live breakdown.
 * Pay Month (open cycle) owns list + arrears. Increment Effective Date is from when CTC applies.
 */
export default function PayrollSalaryRevisionModal({
  isOpen,
  onClose,
  employee,
  structures = [],
  statutoryConfigJson = null,
  branchesList = [],
  darkMode = false,
  onSaved,
  allowMidMonthIncrements = false,
  /** YYYY-MM — open payroll cycle this revision is booked into (list + arrears). */
  bookingPayMonth = null,
}) {
  const [effectiveDate, setEffectiveDate] = useState("");
  const [payMonth, setPayMonth] = useState("");
  const [revisionMode, setRevisionMode] = useState("amount"); // amount | percent
  const [ctcAmount, setCtcAmount] = useState("");
  const [percentValue, setPercentValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [lastRevision, setLastRevision] = useState(null);
  const [existingEffectiveMonths, setExistingEffectiveMonths] = useState(() => new Set());
  const [overlapPrompt, setOverlapPrompt] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [finalizedPayrollMonthSet, setFinalizedPayrollMonthSet] = useState(
    () => new Set()
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canDeleteRevision =
    isAdmin || hasScreenAccess("payroll", "assigned_structure", "delete");
  const [manualOverrides, setManualOverrides] = useState({});
  const canOverrideFormula =
    isAdmin || hasScreenAccess("payroll", "structure_value_override", "edit");
  /** Active structure params + its formula overrides; choice per component: "keep" | "formula" (default). */
  const [activeParams, setActiveParams] = useState({});
  const [activeFormulaOverrides, setActiveFormulaOverrides] = useState({});
  const [formulaOverrideChoices, setFormulaOverrideChoices] = useState({});
  const [localStatutoryConfig, setLocalStatutoryConfig] = useState(statutoryConfigJson);
  const [localBranches, setLocalBranches] = useState(branchesList);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  /** Opt-in Fixed CTC add-ons (NPS / Meal) — from structure snapshot + applicability */
  const [benefitOptIns, setBenefitOptIns] = useState({
    employeeNps: null,
    employeeMeal: null,
  });

  useEffect(() => {
    if (!isOpen || !employee) return;
    setRevisionMode("amount");
    setCtcAmount("");
    setPercentValue("");
    setReason("");
    setShowHistory(false);
    setSalaryHistory([]);
    setLastRevision(null);
    setExistingEffectiveMonths(new Set());
    setOverlapPrompt(null);
    setInfoOpen(false);
    setManualOverrides({});
    setActiveParams({});
    setActiveFormulaOverrides({});
    setFormulaOverrideChoices({});
    setActiveTemplateId(null);
    setBenefitOptIns({ employeeNps: null, employeeMeal: null });
    const eff = incrementEffectiveFloorIso(employee, allowMidMonthIncrements);
    setEffectiveDate(eff);
    setPayMonth("");
  }, [isOpen, employee, allowMidMonthIncrements]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    payrollOpsService
      .listFinalizedPayrollMonths()
      .then((finalRes) => {
        if (cancelled) return;
        const rows = Array.isArray(finalRes?.data) ? finalRes.data : [];
        const set = new Set();
        rows.forEach((r) => {
          const ym = String(r?.payMonth || "").slice(0, 7);
          if (/^\d{4}-\d{2}$/.test(ym)) set.add(ym);
        });
        setFinalizedPayrollMonthSet(set);
      })
      .catch(() => {
        if (!cancelled) setFinalizedPayrollMonthSet(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    let cancelled = false;
    salaryService
      .getSalaryHistory(employee.id)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.allRevisions)
          ? res.allRevisions
          : Array.isArray(res?.data)
            ? res.data
            : [];
        const fullLast = rows[0] || null;
        const last = res?.lastRevision
          ? { ...fullLast, ...res.lastRevision }
          : fullLast;
        setLastRevision(last);
        setExistingEffectiveMonths(
          new Set(rows.map((r) => monthKeyFromIso(r.effectiveDate)).filter(Boolean))
        );
        const lastEff = String(last?.effectiveDate || "").slice(0, 10);
        const raw = lastEff
          ? lastEff
          : incrementEffectiveFloorIso(employee, allowMidMonthIncrements);
        setEffectiveDate(
          lastEff
            ? allowMidMonthIncrements
              ? lastEff
              : toFirstOfMonth(lastEff)
            : raw
        );
        const bookYm = String(last?.bookingPayMonth || "").slice(0, 7);
        if (last && /^\d{4}-\d{2}$/.test(bookYm)) {
          setPayMonth(bookYm);
        } else if (last && lastEff) {
          setPayMonth(monthKeyFromIso(lastEff));
        }
        if (last?.reason) setReason(String(last.reason).trim());
        const prevCtc = Number(last?.previousCtc);
        const newCtcVal = Number(last?.newCtc);
        if (Number.isFinite(prevCtc) && Number.isFinite(newCtcVal)) {
          const delta = Math.round((newCtcVal - prevCtc) * 100) / 100;
          setCtcAmount(String(delta));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLastRevision(null);
        setExistingEffectiveMonths(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employee, allowMidMonthIncrements]);

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const asOf = effectiveDate ? String(effectiveDate).slice(0, 7) : undefined;
        const [activeRes, statRes, branchRes, resolveRes] = await Promise.all([
          salaryStructureService.getActiveStructure(employee.id).catch(() => null),
          statutoryConfigJson
            ? Promise.resolve(null)
            : salaryStructureService.getStatutoryConfig().catch(() => null),
          Array.isArray(branchesList) && branchesList.length
            ? Promise.resolve(null)
            : branchService.getBranches().catch(() => null),
          payrollOpsService
            .resolveStatutoryApplicability(employee.id, asOf)
            .catch(() => null),
        ]);
        if (cancelled) return;
        if (statRes?.data?.configJson) setLocalStatutoryConfig(statRes.data.configJson);
        else if (statutoryConfigJson) setLocalStatutoryConfig(statutoryConfigJson);
        if (branchRes?.success || branchRes?.data) {
          setLocalBranches(branchRes.data || []);
        } else if (Array.isArray(branchesList)) {
          setLocalBranches(branchesList);
        }
        const active = activeRes?.data || null;
        const params =
          active?.parametersJson && typeof active.parametersJson === "string"
            ? (() => {
                try {
                  return JSON.parse(active.parametersJson);
                } catch {
                  return {};
                }
              })()
            : active?.parametersJson || active?.parameters_json || {};
        const tplId =
          active?.templateId ??
          active?.template_id ??
          params?.structureId ??
          null;
        if (tplId != null && tplId !== "") setActiveTemplateId(Number(tplId));
        else setActiveTemplateId(null);

        const byActive =
          tplId != null
            ? (structures || []).find((s) => Number(s.id) === Number(tplId))
            : null;
        const structure =
          byActive || findApplicableSalaryStructure(employee, structures);
        if (structure && active) {
          setManualOverrides(extractManualOverridesFromSnapshot(active, structure));
        }
        setActiveParams(params || {});
        setActiveFormulaOverrides(
          structure &&
            params?.formulaOverrides &&
            typeof params.formulaOverrides === "object" &&
            Number(tplId) === Number(structure.id)
            ? params.formulaOverrides
            : {}
        );

        const addons = params.structureAddons || params || {};
        const resolvedBenefits = resolveRes?.data?.benefits || resolveRes?.benefits || null;
        const npsFromResolve = resolvedBenefits?.employerNps;
        const mealFromResolve = resolvedBenefits?.mealVoucher;
        const npsFromSnap = addons.employeeNps || null;
        const mealFromSnap = addons.employeeMealVoucher || addons.employeeMeal || null;
        setBenefitOptIns({
          employeeNps:
            npsFromResolve && typeof npsFromResolve === "object"
              ? { ...npsFromResolve, enabled: npsFromResolve.enabled === true }
              : npsFromSnap
                ? { enabled: false, ...npsFromSnap }
                : null,
          employeeMeal:
            mealFromResolve && typeof mealFromResolve === "object"
              ? { ...mealFromResolve, enabled: mealFromResolve.enabled === true }
              : mealFromSnap
                ? { enabled: false, ...mealFromSnap }
                : null,
        });
      } catch {
        /* ignore hydrate errors — preview still works from template */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, employee?.id, structures, statutoryConfigJson, branchesList, effectiveDate]);

  const currentCtc = Number(employee?.ctc || employee?.ctcAmount || 0);
  const employeeDisplayName =
    [employee?.firstName, employee?.lastName, employee?.user?.name]
      .filter(Boolean)
      .join(" ") || "Employee";

  // Prefer already-assigned active structure; fall back to assignment-rule match.
  const matchedStructure = useMemo(() => {
    if (!employee) return null;
    if (activeTemplateId != null) {
      const byId = (structures || []).find(
        (s) => Number(s.id) === Number(activeTemplateId)
      );
      if (byId) return byId;
    }
    return findApplicableSalaryStructure(employee, structures);
  }, [employee, structures, activeTemplateId]);
  const isManualStructure = structureRequiresManualInput(matchedStructure);
  const manualComponents = useMemo(
    () => listManualFixedComponents(matchedStructure),
    [matchedStructure]
  );

  const revisedCtc = useMemo(() => {
    if (revisionMode === "percent") {
      if (percentValue === "" || percentValue == null) return 0;
      const pct = Number(String(percentValue).replace(/,/g, ""));
      if (!Number.isFinite(pct) || currentCtc <= 0) return 0;
      return Math.round(currentCtc * (1 + pct / 100) * 100) / 100;
    }
    // Empty increment = not entered yet ("" must not become +0)
    if (ctcAmount === "" || ctcAmount == null) return 0;
    const increment = Number(String(ctcAmount).replace(/,/g, ""));
    if (!Number.isFinite(increment) || currentCtc <= 0) return 0;
    return Math.round((currentCtc + increment) * 100) / 100;
  }, [revisionMode, percentValue, ctcAmount, currentCtc]);

  // Show current package until a revised CTC is entered.
  const previewCtc = revisedCtc > 0 ? revisedCtc : currentCtc;

  const percentageChange = calculatePercentageChange(currentCtc, revisedCtc);
  const absPercentage =
    percentageChange != null ? Math.abs(percentageChange).toFixed(2) : null;
  const isIncrease = percentageChange != null && percentageChange > 0;
  const isDecrease = percentageChange != null && percentageChange < 0;
  const amountWords = previewCtc > 0 ? numberToWords(previewCtc) : "";
  const deltaAbs = revisedCtc > 0 ? Math.abs(revisedCtc - currentCtc) : 0;

  const payMonthOptions = useMemo(
    () =>
      buildPayMonthOptions({
        finalizedSet: finalizedPayrollMonthSet,
        effectiveYm: monthKeyFromIso(effectiveDate),
        bookingYm: String(bookingPayMonth || currentYm()).slice(0, 7),
      }),
    [finalizedPayrollMonthSet, effectiveDate, bookingPayMonth]
  );

  useEffect(() => {
    if (!isOpen) return;
    const selectable = payMonthOptions.filter((o) => o.selectable);
    if (!selectable.length) return;

    if (lastRevision?.id) {
      if (payMonth) return;
      const next = resolveDefaultPayMonth({
        lastRevision,
        effectiveDate,
        payMonthOptions,
        bookingPayMonth,
      });
      if (next) setPayMonth(next);
      return;
    }

    // First increment from joining — Pay Month tracks Increment Effective month
    const next = resolveDefaultPayMonth({
      lastRevision: null,
      effectiveDate,
      payMonthOptions,
      bookingPayMonth,
    });
    if (next && next !== payMonth) setPayMonth(next);
  }, [
    isOpen,
    payMonthOptions,
    bookingPayMonth,
    payMonth,
    effectiveDate,
    lastRevision,
  ]);

  const keptFormulaOverrides = useMemo(() => {
    if (!canOverrideFormula) return {};
    return Object.fromEntries(
      Object.entries(activeFormulaOverrides || {}).filter(
        ([id]) => formulaOverrideChoices[id] === "keep"
      )
    );
  }, [activeFormulaOverrides, formulaOverrideChoices, canOverrideFormula]);

  const employeeWithOptIns = useMemo(
    () =>
      employee
        ? {
            ...employee,
            employeeNps: benefitOptIns.employeeNps || employee?.employeeNps || null,
            employeeMealVoucher:
              benefitOptIns.employeeMeal ||
              employee?.employeeMealVoucher ||
              employee?.employeeMeal ||
              null,
            employeeMeal:
              benefitOptIns.employeeMeal ||
              employee?.employeeMeal ||
              employee?.employeeMealVoucher ||
              null,
          }
        : null,
    [employee, benefitOptIns]
  );

  const preview = useMemo(() => {
    if (!isOpen || !employee || !previewCtc || !matchedStructure) return null;

    // Manual: use entered monthly amounts as-is (never silent CTC-scale).
    // Auto: empty overrides — formulas derive from revised CTC (unless HR chose Keep on an override).
    const overrides = isManualStructure ? manualOverrides || {} : {};
    const withOverrides = applyFormulaOverrides(
      applyManualOverrides(matchedStructure, overrides),
      keptFormulaOverrides
    );
    return buildEmployeeStructureBreakdown({
      structure: withOverrides,
      annualAmount: previewCtc,
      salaryType: "ctc",
      employee: { ...employeeWithOptIns, ctc: previewCtc },
      statutoryConfigJson: localStatutoryConfig,
      branchesList: localBranches,
      effectiveMonth: effectiveDate ? effectiveDate.slice(0, 7) : undefined,
    });
  }, [
    isOpen,
    employee,
    employeeWithOptIns,
    matchedStructure,
    isManualStructure,
    previewCtc,
    manualOverrides,
    keptFormulaOverrides,
    localStatutoryConfig,
    localBranches,
    effectiveDate,
  ]);

  const handleManualAmountChange = ({ name, componentId, value }) => {
    setManualOverrides((prev) => {
      const next = { ...prev };
      if (componentId != null && componentId !== "") {
        next[String(componentId)] = value;
      }
      if (name) next[String(name)] = value;
      return next;
    });
  };

  if (!isOpen || !employee) return null;

  const applyIncrementDate = (raw) => {
    setEffectiveDate(
      clampIncrementEffectiveIso(raw, employee, allowMidMonthIncrements)
    );
  };

  const incrementEffectiveMinAttr = allowMidMonthIncrements
    ? resolveEmployeeMinDateValue(employee) || undefined
    : resolveEmployeeMinMonthValue(employee) || undefined;

  const switchMode = (mode) => {
    if (mode === revisionMode) return;
    if (mode === "percent") {
      const increment = Number(String(ctcAmount).replace(/,/g, "")) || 0;
      const nextRevised = currentCtc > 0 ? currentCtc + increment : 0;
      const pct = calculatePercentageChange(currentCtc, nextRevised);
      setPercentValue(pct != null ? String(Number(pct.toFixed(2))) : "");
    } else if (revisedCtc > 0 && currentCtc > 0) {
      setCtcAmount(String(Math.round((revisedCtc - currentCtc) * 100) / 100));
    } else {
      setCtcAmount("");
    }
    setRevisionMode(mode);
  };

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const response = await salaryService.getSalaryHistory(employee.id);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const timeline = Array.isArray(response?.timeline) ? response.timeline : [];
      const timelineByRevisionId = new Map(
        timeline
          .filter((t) => t.salaryRevisionId)
          .map((t) => [Number(t.salaryRevisionId), t])
      );
      setSalaryHistory(
        rows.map((item) => {
          const linked = timelineByRevisionId.get(Number(item.id));
          return {
            id: item.id,
            effectiveDate: item.effectiveDate,
            arrearsEffectiveDate: item.arrearsEffectiveDate,
            bookingPayMonth: item.bookingPayMonth || null,
            previousCtc: item.previousCtc,
            ctcAmount: item.newCtc,
            reason: item.reason || "—",
            designation:
              item.newDesignation?.name ||
              item.previousDesignation?.name ||
              "—",
            createdDate: item.createdAt || item.actionDate,
            createdBy: formatCreatorName(item.creator),
            structureId: linked?.structureId || null,
            canDelete: item.canDelete ?? linked?.canDelete ?? false,
            deleteBlockReason:
              item.deleteBlockReason || linked?.deleteBlockReason || null,
          };
        })
      );
    } catch (err) {
      console.error("Error loading salary history:", err);
      toast.error("Failed to load salary history");
      setSalaryHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const exportHistoryCsv = () => {
    if (!salaryHistory.length) {
      toast.error("No history to export");
      return;
    }
    const exportData = salaryHistory.map((item) => {
      const prev = Number(item.previousCtc);
      const pct = Number.isFinite(prev)
        ? calculatePercentageChange(prev, item.ctcAmount)
        : null;
      let percentageText = "—";
      if (pct != null) {
        if (pct > 0) percentageText = `${Math.abs(pct).toFixed(2)}% Increase`;
        else if (pct < 0) percentageText = `${Math.abs(pct).toFixed(2)}% Decrease`;
        else percentageText = "No Change";
      }
      return {
        "Effective Date": formatDisplayDate(item.effectiveDate),
        "Pay Month": item.bookingPayMonth ? formatMonthLabel(item.bookingPayMonth) : "—",
        "Arrears Effective Date": formatDisplayDate(item.arrearsEffectiveDate),
        "Previous CTC": Number.isFinite(Number(item.previousCtc))
          ? formatIndianNumber(item.previousCtc)
          : "—",
        "CTC Amount": item.ctcAmount ? formatIndianNumber(item.ctcAmount) : "—",
        "Amount in Words": item.ctcAmount
          ? `Rs. ${numberToWords(item.ctcAmount)}`
          : "—",
        "Percentage Change": percentageText,
        Reason: item.reason,
        Designation: item.designation,
        "Created Date": formatDisplayDate(item.createdDate),
        "Created By": item.createdBy,
      };
    });
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => `"${String(row[header]).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `salary_history_${employee.empCode || employee.employeeCode || employee.id}_${todayIso()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Salary history exported");
  };

  const handleSave = async (opts = {}) => {
    if (!effectiveDate) {
      toast.error("Increment Effective Date is required");
      return;
    }
    if (!allowMidMonthIncrements && effectiveDate.slice(8, 10) !== "01") {
      toast.error(
        "Mid-month increments are disabled. Choose the 1st of the month, or enable “Allow mid month Increments” in Payroll Settings."
      );
      return;
    }
    if (revisionMode === "percent") {
      const pct = Number(String(percentValue).replace(/,/g, ""));
      if (!Number.isFinite(pct)) {
        toast.error("Enter a valid percentage");
        return;
      }
      if (currentCtc <= 0) {
        toast.error("Current CTC is required to revise by percentage");
        return;
      }
    } else {
      const increment = Number(String(ctcAmount).replace(/,/g, ""));
      if (!Number.isFinite(increment)) {
        toast.error("Enter a valid increment amount");
        return;
      }
      if (currentCtc <= 0) {
        toast.error("Current CTC is required to revise by amount");
        return;
      }
    }
    if (!revisedCtc || revisedCtc <= 0) {
      toast.error("Enter a valid revised CTC");
      return;
    }
    if (Math.abs(Number(revisedCtc) - Number(currentCtc)) < 1) {
      toast.error(
        "Revised CTC must differ from Current CTC. A No Change save would overwrite a prior increment on the same effective date."
      );
      return;
    }
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (!payMonth || !/^\d{4}-\d{2}$/.test(payMonth)) {
      toast.error("Pay Month is required");
      return;
    }
    if (payMonth < monthKeyFromIso(effectiveDate)) {
      toast.error(
        "Pay Month must be the same as Increment Effective month or a later unfinalised month"
      );
      return;
    }
    const chosen = payMonthOptions.find((o) => o.value === payMonth);
    if (!chosen) {
      toast.error("Pay Month must be an open (not finalised) payroll month on or after the Increment Effective month");
      return;
    }

    const overlapping = existingEffectiveMonths.has(monthKeyFromIso(effectiveDate));
    const confirmContinue = !!(opts.confirmSameEffectiveContinue || overlapPrompt?.confirmed);
    if (overlapping && !confirmContinue) {
      setOverlapPrompt({
        confirmed: false,
        effectiveMonth: monthKeyFromIso(effectiveDate),
        existingCtc: lastRevision?.newCtc,
        existingDate: lastRevision?.effectiveDate || effectiveDate,
      });
      return;
    }

    let structureSnapshot = null;
    let overridesToSend = null;
    if (isManualStructure && matchedStructure) {
      // Use the same amounts the UI shows (override → breakdown monthly → template).
      // 0 is valid; blank / non-numeric is not.
      const fallbackByName = {};
      [...(preview?.earningsList || []), ...(preview?.reimbursementsList || [])].forEach(
        (row) => {
          const n = String(row?.name || "").trim();
          if (n && Number.isFinite(Number(row.amount))) fallbackByName[n] = Number(row.amount);
        }
      );
      const collected = buildCompleteManualOverrides(
        manualComponents,
        manualOverrides,
        fallbackByName
      );
      if (!collected.ok) {
        toast.error(
          `Enter monthly amounts for Manual components: ${collected.missing
            .map((c) => manualFixedColumnLabel(c))
            .join(", ")}`
        );
        return;
      }
      overridesToSend = collected.overrides;
      const built = buildEmployeeStructureSavePayload({
        structure: matchedStructure,
        employee: { ...employee, ctc: revisedCtc },
        overrides: overridesToSend,
        formulaOverrides: keptFormulaOverrides,
        statutoryConfigJson: localStatutoryConfig,
        branchesList: localBranches,
        effectiveFrom: effectiveDate,
        changeNote: `Salary revision Manual amounts (${currentCtc} → ${revisedCtc})`,
      });
      if (!built.ok) {
        toast.error(
          built.message ||
            "Manual component amounts do not fit the revised CTC. Adjust amounts or add Balance CTC."
        );
        return;
      }
      structureSnapshot = built.payload;
    } else if (matchedStructure && Object.keys(keptFormulaOverrides).length) {
      const built = buildEmployeeStructureSavePayload({
        structure: matchedStructure,
        employee: { ...employeeWithOptIns, ctc: revisedCtc },
        overrides: {},
        formulaOverrides: keptFormulaOverrides,
        statutoryConfigJson: localStatutoryConfig,
        branchesList: localBranches,
        effectiveFrom: effectiveDate,
        changeNote: `Salary revision (${currentCtc} → ${revisedCtc}) · kept overrides: ${Object.values(
          keptFormulaOverrides
        )
          .map((o) => `${o.name} ₹${o.amount}`)
          .join("; ")}`,
      });
      if (!built.ok) {
        toast.error(
          built.message ||
            "Kept override amounts do not fit the revised CTC — choose Back to formula."
        );
        return;
      }
      // Keep NPS / VPF / Meal opt-ins and PB from the active structure.
      structureSnapshot = {
        ...built.payload,
        parametersJson: { ...activeParams, ...built.payload.parametersJson },
      };
    }

    const actionDate = todayIso();
    setSaving(true);
    try {
      const payload = {
        employeeId: employee.id,
        actionDate,
        effectiveDate,
        arrearsEffectiveDate: effectiveDate,
        newCtc: revisedCtc,
        reason: reason.trim(),
        designationChanged: false,
        bookingPayMonth: payMonth,
        payMonth,
        confirmSameEffectiveContinue: confirmContinue,
        remarks:
          revisionMode === "percent"
            ? `Revision by %age: ${String(percentValue).trim()}%`
            : `Revision by Amount: +${String(ctcAmount).trim()}`,
        ...(overridesToSend ? { manualOverrides: overridesToSend } : {}),
        ...(structureSnapshot ? { structureSnapshot } : {}),
      };

      let createRes;
      try {
        createRes = await salaryService.createSalary(payload);
      } catch (firstErr) {
        const data = firstErr?.response?.data;
        if (data?.code === "SAME_EFFECTIVE_REVISION_EXISTS" && !payload.confirmSameEffectiveContinue) {
          setOverlapPrompt({
            confirmed: false,
            effectiveMonth:
              data?.existing?.effectiveMonth || monthKeyFromIso(effectiveDate),
            existingCtc: data?.existing?.newCtc,
            existingDate: data?.existing?.effectiveDate || effectiveDate,
          });
          return;
        }
        throw firstErr;
      }

      const refresh = createRes?.structureRefresh;
      if (refresh?.pendingManual || refresh?.reason === "manual_breakdown_missing") {
        toast(
          "CTC revised. Manual component amounts were empty — open Profile → Salary Structure (or Manual Component Import) before Process.",
          { icon: "⚠️" }
        );
      } else if (refresh && refresh.ok === false) {
        toast.error(
          refresh.reason
            ? `CTC saved but structure snapshot refresh failed (${refresh.reason}).`
            : "CTC saved but structure snapshot could not be refreshed."
        );
      }

      toast.success("Salary revision saved");
      setOverlapPrompt(null);
      onSaved?.({
        employeeId: employee.id,
        newCtc: revisedCtc,
        effectiveDate,
        actionDate,
        bookingPayMonth: payMonth,
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Revision failed");
    } finally {
      setSaving(false);
    }
  };

  const panel = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const inputCls = `w-full rounded border px-3 py-2 text-sm ${
    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))]"
  }`;

  return (
    <>
    <ActionFormOverlay onClose={onClose}>
      <ActionFormShell className={panel} maxWidthClass="w-[min(100%,920px)]">
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-primary)/0.15)] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold">Salary Revision</h3>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              {employeeDisplayName}
              {employee.employeeCode || employee.empCode
                ? ` (${employee.employeeCode || employee.empCode})`
                : ""}
              {" · "}Current CTC: ₹{currentCtc.toLocaleString("en-IN")}
              {matchedStructure ? (
                <>
                  {" · "}
                  <span
                    className={
                      isManualStructure
                        ? "text-[hsl(var(--ds-warning))]"
                        : "text-[hsl(var(--ds-primary))]"
                    }
                  >
                    {structureCalcModeLabel(matchedStructure)}
                  </span>
                </>
              ) : null}
            </p>
            {currentCtc > 0 && (
              <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Rs. {numberToWords(currentCtc)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setInfoOpen((open) => !open);
                }}
                onBlur={() => setInfoOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Salary revision details"
                aria-expanded={infoOpen}
              >
                <DsIcon icon={Info} size="sm" />
              </button>
              {infoOpen ? (
                <div
                  className="absolute right-0 top-full z-20 mt-1 w-72 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 text-left text-xs leading-snug text-[hsl(var(--ds-muted-fg))] shadow-lg sm:w-80"
                  role="tooltip"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <ul className="space-y-2">
                      <li>
                      Increment Effective {allowMidMonthIncrements ? "Date" : "Month"} defaults to the last revision, or — if none — the later of Date of Joining and company service commencement.
                    </li>
                    <li>
                      You cannot pick an Increment Effective month before company commencement or before the employee&apos;s Date of Joining.
                    </li>
                    <li>
                      Pay Month must be the same as the Increment Effective month, or a later unfinalised payroll month. Earlier months cannot be selected.
                    </li>
                    <li>
                      Same-month effective date shows a warning. Continue adds another increment on current CTC; arrears cover the differential from that month through the month before Pay Month.
                    </li>
                    <li>
                      {allowMidMonthIncrements
                        ? "Mid-month increments are allowed — any calendar day can be selected."
                        : "Mid-month is off — the date is forced to the 1st. Enable in Payroll Settings → General if needed."}
                    </li>
                    <li>
                      {isManualStructure
                        ? "Manual structure — edit MONTHLY amounts in Salary Breakdown (E) below (same as Profile)."
                        : "Auto (Formula) — breakdown is derived from revised CTC."}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <button type="button" onClick={onClose} className="rounded-full p-1 opacity-70 hover:opacity-100">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium">
                {allowMidMonthIncrements
                  ? "Increment Effective Date"
                  : "Increment Effective Month"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type={allowMidMonthIncrements ? "date" : "month"}
                value={
                  allowMidMonthIncrements
                    ? effectiveDate
                    : monthKeyFromIso(effectiveDate)
                }
                min={incrementEffectiveMinAttr}
                onChange={(e) => applyIncrementDate(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">
                Pay Month <span className="text-red-500">*</span>
              </label>
              <select
                value={payMonth}
                onChange={(e) => setPayMonth(e.target.value)}
                className={inputCls}
              >
                <option value="">Select pay month</option>
                {payMonthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={inputCls}
              >
                <option value="">Select Reason</option>
                {REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
            <div>
              <label className="mb-1.5 block text-xs font-medium">
                Revision type <span className="text-red-500">*</span>
              </label>
              <div className="inline-flex rounded-lg border border-[hsl(var(--ds-border))] p-0.5">
                <button
                  type="button"
                  onClick={() => switchMode("amount")}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    revisionMode === "amount"
                      ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  Amount
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("percent")}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    revisionMode === "percent"
                      ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  %age
                </button>
              </div>
            </div>
            <div>
              {revisionMode === "amount" ? (
                <>
                  <label className="mb-1 block text-xs font-medium">
                    Increment amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ctcAmount ? formatIndianNumber(ctcAmount) : ""}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (raw === "" || /^-?\d+(\.\d*)?$/.test(raw)) setCtcAmount(raw);
                    }}
                    className={inputCls}
                    placeholder="Amount to add to current CTC (e.g. 25000)"
                  />
                  <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    Revised CTC = Current CTC + Amount
                    {revisedCtc > 0
                      ? ` → ₹${revisedCtc.toLocaleString("en-IN")}`
                      : ""}
                  </p>
                </>
              ) : (
                <>
                  <label className="mb-1 block text-xs font-medium">
                    Revision %age <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={percentValue}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "");
                        if (raw === "" || /^-?\d+(\.\d*)?$/.test(raw)) setPercentValue(raw);
                      }}
                      className={`${inputCls} pr-8`}
                      placeholder="e.g. 10 for 10% increase"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--ds-muted-fg))]">
                      %
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    Revised CTC = Current CTC × (1 + % / 100)
                    {revisedCtc > 0
                      ? ` → ₹${revisedCtc.toLocaleString("en-IN")}`
                      : ""}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-[hsl(var(--ds-secondary))]">
              {amountWords ? `Rs. ${amountWords}` : ""}
            </span>
            {percentageChange != null && (
              <span
                className={`text-xs font-semibold ${
                  isIncrease
                    ? "text-emerald-600"
                    : isDecrease
                      ? "text-red-600"
                      : "text-[hsl(var(--ds-muted-fg))]"
                }`}
              >
                {isIncrease
                  ? `${absPercentage}% Increase`
                  : isDecrease
                    ? `${absPercentage}% Decrease`
                    : "No Change"}
                {deltaAbs > 0 && (
                  <span className="ml-2 font-normal opacity-80">
                    (Δ ₹{deltaAbs.toLocaleString("en-IN")})
                  </span>
                )}
              </span>
            )}
          </div>

          {Object.keys(activeFormulaOverrides).length ? (
            <div className="space-y-2 rounded-lg border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.08)] p-3">
              <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Overridden components</p>
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                These amounts were overridden on the current structure. Keep carries the amount into the
                revised structure (Special Allowance absorbs the rest); Back to formula recalculates it from
                the revised CTC.
                {!canOverrideFormula ? " You need 'Override Structure Values' access to keep them." : ""}
              </p>
              {Object.entries(activeFormulaOverrides).map(([id, o]) => {
                const choice = canOverrideFormula && formulaOverrideChoices[id] === "keep" ? "keep" : "formula";
                return (
                  <div key={id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="min-w-0">
                      {o.name || `Component ${id}`} — ₹{Number(o.amount || 0).toLocaleString("en-IN")}
                      <span className="ml-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        (formula was ₹{Number(o.formulaAmount || 0).toLocaleString("en-IN")}
                        {o.reason ? ` · ${o.reason}` : ""})
                      </span>
                    </span>
                    <div className="inline-flex overflow-hidden rounded-md border border-[hsl(var(--ds-border))]">
                      {[
                        ["formula", "Back to formula"],
                        ["keep", "Keep"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={choice === value}
                          disabled={value === "keep" && !canOverrideFormula}
                          onClick={() =>
                            setFormulaOverrideChoices((prev) => ({ ...prev, [id]: value }))
                          }
                          className={`px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50 ${
                            choice === value
                              ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                              : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {preview ? (
            <SalaryStructureCalculationResults
              earningsList={preview.earningsList}
              benefitsList={preview.benefitsList}
              reimbursementsList={preview.reimbursementsList}
              deductionsList={preview.deductionsList}
              employerList={preview.employerList}
              statutoryDeductionsList={preview.statutoryDeductionsList}
              statutoryEmployerList={preview.statutoryEmployerList}
              totals={preview.totals}
              emptyMessage="No components"
              onManualAmountChange={
                isManualStructure ? handleManualAmountChange : null
              }
              draftAmounts={isManualStructure ? manualOverrides : null}
            />
          ) : (
            <p className="text-sm text-amber-700">
              {!matchedStructure
                ? "Cannot preview — no salary structure assigned for this employee. Open Assigned Structure / Profile and save a structure first."
                : !previewCtc
                  ? "Cannot preview — employee CTC must be greater than 0."
                  : "Cannot preview — assign a salary structure and enter a positive CTC."}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            onClick={openHistory}
            disabled={loadingHistory}
            loading={loadingHistory}
          >
            <History size={14} />
            {loadingHistory ? "Loading…" : "View History"}
          </DsButton>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-lg px-4 py-2 text-sm ${
                darkMode ? "bg-[hsl(var(--ds-muted))]" : "border border-[hsl(var(--ds-border))]"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save revision"}
            </button>
          </div>
        </div>
      </ActionFormShell>
    </ActionFormOverlay>

      {showHistory && (
        <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4">
          <div
            className={`flex h-[80%] w-[min(100%,1100px)] flex-col overflow-hidden rounded-xl border shadow-2xl ${panel}`}
          >
            <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
              <div>
                <h3 className="text-base font-semibold">Salary Revision History</h3>
                <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                  {employeeDisplayName}
                  {employee.empCode || employee.employeeCode
                    ? ` (${employee.empCode || employee.employeeCode})`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="rounded-full p-1 opacity-70 hover:opacity-100"
                aria-label="Close history"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-hidden px-5 py-4">
              {loadingHistory ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading history…</p>
              ) : salaryHistory.length === 0 ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                  No salary revision history found for this employee.
                </p>
              ) : (
                <div className="h-full overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))]">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Effective Date</th>
                        <th className="px-3 py-2 text-left font-medium">Pay Month</th>
                        <th className="px-3 py-2 text-left font-medium">Arrears Date</th>
                        <th className="px-3 py-2 text-right font-medium">Previous CTC</th>
                        <th className="px-3 py-2 text-right font-medium">Revised CTC</th>
                        <th className="px-3 py-2 text-left font-medium">Amount in Words</th>
                        <th className="px-3 py-2 text-left font-medium">Change</th>
                        <th className="px-3 py-2 text-left font-medium">Reason</th>
                        <th className="px-3 py-2 text-left font-medium">Designation</th>
                        <th className="px-3 py-2 text-left font-medium">Created</th>
                        <th className="px-3 py-2 text-left font-medium">Created By</th>
                        {canDeleteRevision ? (
                          <th className="px-3 py-2 text-left font-medium">Actions</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {salaryHistory.map((item) => {
                        const prev = Number(item.previousCtc);
                        const pct =
                          Number.isFinite(prev) && prev > 0
                            ? calculatePercentageChange(prev, item.ctcAmount)
                            : null;
                        return (
                          <tr
                            key={item.id}
                            className="border-t border-[hsl(var(--ds-border))] align-top"
                          >
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatDisplayDate(item.effectiveDate)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {item.bookingPayMonth
                                ? formatMonthLabel(item.bookingPayMonth)
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatDisplayDate(item.arrearsEffectiveDate)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                              {Number.isFinite(prev)
                                ? `₹${formatIndianNumber(prev)}`
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">
                              {item.ctcAmount
                                ? `₹${formatIndianNumber(item.ctcAmount)}`
                                : "—"}
                            </td>
                            <td className="max-w-[180px] px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                              {item.ctcAmount
                                ? `Rs. ${numberToWords(item.ctcAmount)}`
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {pct == null ? (
                                "—"
                              ) : (
                                <span
                                  className={`text-xs font-semibold ${
                                    pct > 0
                                      ? "text-emerald-600"
                                      : pct < 0
                                        ? "text-red-600"
                                        : "text-[hsl(var(--ds-muted-fg))]"
                                  }`}
                                >
                                  {pct > 0
                                    ? `${Math.abs(pct).toFixed(2)}% Increase`
                                    : pct < 0
                                      ? `${Math.abs(pct).toFixed(2)}% Decrease`
                                      : "No Change"}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">{displayLabel(item.reason)}</td>
                            <td className="px-3 py-2">{displayLabel(item.designation)}</td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatDisplayDate(item.createdDate)}
                            </td>
                            <td className="px-3 py-2">{item.createdBy}</td>
                            {canDeleteRevision ? (
                              <td className="px-3 py-2">
                                {item.canDelete ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--ds-danger))] hover:underline"
                                    onClick={() =>
                                      setDeleteTarget({
                                        structureId: item.structureId,
                                        salaryRevisionId: item.id,
                                      })
                                    }
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                ) : (
                                  <span
                                    className="max-w-[160px] text-[10px] leading-snug text-[hsl(var(--ds-warning))]"
                                    title={
                                      item.deleteBlockReason ||
                                      "Only the latest revision can be deleted"
                                    }
                                  >
                                    {item.deleteBlockReason ||
                                      "Delete unavailable (newest first / unprocess month)"}
                                  </span>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={exportHistoryCsv}
                disabled={!salaryHistory.length}
              >
                Export to Excel
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setShowHistory(false)}
              >
                Close
              </DsButton>
            </div>
          </div>
        </div>
      )}

      {overlapPrompt ? (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/50 p-4">
          <div
            className={`w-full max-w-md rounded-xl border p-5 shadow-2xl ${panel}`}
            role="dialog"
            aria-labelledby="same-effective-warning-title"
          >
            <h3 id="same-effective-warning-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
              Same month effective date
            </h3>
            <p className="mt-2 text-sm text-[hsl(var(--ds-muted-fg))]">
              This employee already has a salary revision effective in{" "}
              <strong>{formatMonthLabel(overlapPrompt.effectiveMonth)}</strong>
              {overlapPrompt.existingCtc != null
                ? ` (revised CTC ₹${Number(overlapPrompt.existingCtc).toLocaleString("en-IN")})`
                : ""}
              . Do you still want to continue with the same effective month?
            </p>
            <p className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">
              Continue keeps the same Increment Effective Date and adds this increment on current CTC.
              Arrears for the differential will be calculated from that month through the month before{" "}
              {payMonth ? formatMonthLabel(payMonth) : "Pay Month"}.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOverlapPrompt(null)}
              >
                Change date
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                loading={saving}
                disabled={saving}
                onClick={() => {
                  setOverlapPrompt((prev) => (prev ? { ...prev, confirmed: true } : prev));
                  handleSave({ confirmSameEffectiveContinue: true });
                }}
              >
                Continue
              </DsButton>
            </div>
          </div>
        </div>
      ) : null}

      <StructureRevisionDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        employeeId={employee?.id}
        structureId={deleteTarget?.structureId}
        salaryRevisionId={deleteTarget?.salaryRevisionId}
        onDeleted={() => {
          openHistory();
          onSaved?.();
        }}
      />
    </>
  );
}
